import { useState, useRef, useMemo } from "react";
import { createSearchParams, Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import Fuse from "fuse.js";
import { X, FileText, Search } from "lucide-react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { DocumentVersionStatus } from "~/config/types";
import { featureFlags } from "~/config/featureFlags";
import { useDocumentVersions } from "~/api/v2/useDocumentVersions";
import { type DocumentVersionSummary } from "~/api/types";
import { type SearchHit, searchDocuments } from "~/api/v2/requests";
import DocumentIcon from "~/molecules/DocumentIcon";
import { FieldStatus } from "~/molecules/FieldStatus";
import { Skeleton } from "~/atoms/skeleton";
import { Button } from "~/atoms/button";
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/atoms/command";
import { Popover, PopoverContent, PopoverTrigger } from "~/atoms/popover";
import { useDebounce } from "~/api/utils";
import { toComparableString } from "~/helpers/string";

// Highlights query matches and keeps <em> segments bold.
function HighlightText({
  text,
  searchWords,
}: {
  text: string;
  searchWords: string[];
}) {
  const escapedSearchWords = searchWords
    .filter((w) => w.length > 0)
    .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));

  const queryMatchRegex =
    escapedSearchWords.length > 0
      ? new RegExp(`(${escapedSearchWords.join("|")})`, "gi")
      : null;

  const emTagPattern = /^<em>(.*)<\/em>$/;
  const styledParts = text.split(/(<em>.*?<\/em>)/g);

  return (
    <>
      {styledParts.flatMap((part, partIdx) => {
        const emMatch = emTagPattern.exec(part);
        const isEmphasized = Boolean(emMatch);
        const plainText = emMatch ? emMatch[1] : part;

        if (!plainText) return [];

        const pieces = queryMatchRegex
          ? plainText.split(queryMatchRegex)
          : [plainText];

        return pieces.map((piece, pieceIdx) => {
          const key = `${partIdx}-${pieceIdx}`;
          const isQueryMatch = queryMatchRegex !== null && pieceIdx % 2 === 1;

          if (isQueryMatch) {
            return (
              <mark
                key={key}
                className={[
                  "bg-warning/20 rounded-sm",
                  isEmphasized ? "font-semibold" : "",
                ].join(" ")}
              >
                {piece}
              </mark>
            );
          }

          return (
            <span
              key={key}
              className={isEmphasized ? "font-semibold" : undefined}
            >
              {piece}
            </span>
          );
        });
      })}
    </>
  );
}

function SearchSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-muted-foreground px-3 pt-2 pb-1 text-xs font-semibold tracking-wide uppercase">
        {title}
      </div>
      <div className="max-h-100 min-h-16 overflow-y-auto">{children}</div>
    </div>
  );
}

function SemanticResultSkeleton() {
  return (
    <div className="flex flex-col gap-3 p-2">
      {[1, 2].map((i) => (
        <div key={i} className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <Skeleton className="size-4 shrink-0" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
        </div>
      ))}
    </div>
  );
}

function getHeadingPath(hit: SearchHit): string {
  return [hit.h2, hit.h3, hit.h4, hit.h5, hit.h6]
    .filter((heading): heading is string => Boolean(heading))
    .join(" -> ");
}

export const TopNavSearch = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const isDocumentSearchEnabled = useFeatureFlagEnabled(
    featureFlags.documentSearch,
  );
  const isRefNumEnabled = useFeatureFlagEnabled(featureFlags.referenceNumber);

  const [semanticResults, setSemanticResults] = useState<SearchHit[]>([]);
  const [semanticLoading, setSemanticLoading] = useState(false);
  const requestIdRef = useRef(0);
  const latestQueryRef = useRef("");

  const { data: documents } = useDocumentVersions({
    statuses: [DocumentVersionStatus.published, DocumentVersionStatus.draft],
  });

  const normalizedDocuments = useMemo(
    () =>
      (documents ?? []).map((d) => ({
        ...d,
        title: toComparableString(d.title),
      })),
    [documents],
  );

  const fuseKeys = useMemo(
    () => (isRefNumEnabled ? ["title", "document.referenceNumber"] : ["title"]),
    [isRefNumEnabled],
  );

  const fuse = useMemo(
    () => new Fuse(normalizedDocuments, { keys: fuseKeys, threshold: 0.3 }),
    [normalizedDocuments, fuseKeys],
  );

  const results = search
    ? fuse.search(toComparableString(search)).map((r) => r.item)
    : [];

  const searchWords = useMemo(
    () =>
      search
        .split(/\s+/)
        .map((w) => w.replaceAll("?", ""))
        .filter((w) => w.length > 1),
    [search],
  );

  const debouncedSearch = useDebounce((query: string) => {
    if (query !== latestQueryRef.current) return;

    if (!query.trim() || !isDocumentSearchEnabled) {
      requestIdRef.current++;
      setSemanticResults([]);
      setSemanticLoading(false);
      return;
    }

    setSemanticLoading(true);
    const id = ++requestIdRef.current;
    const fetchResults = async () => {
      try {
        const hits = await searchDocuments(query);
        if (id !== requestIdRef.current) return;
        setSemanticResults(hits);
      } catch {
        if (id !== requestIdRef.current) return;
        setSemanticResults([]);
      } finally {
        if (id === requestIdRef.current) {
          setSemanticLoading(false);
        }
      }
    };
    void fetchResults();
  }, 300);

  const handleSelect = (doc: DocumentVersionSummary) => {
    latestQueryRef.current = "";
    void navigate({
      pathname: `/documents/${doc.document.id}`,
      search: `?${createSearchParams({ versionId: String(doc.id) }).toString()}`,
    });
    setSearch("");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Command shouldFilter={false} className="relative w-full max-w-80">
        <PopoverTrigger asChild>
          <div className="relative">
            <CommandInput
              ref={inputRef}
              className="pr-7"
              placeholder={
                isDocumentSearchEnabled
                  ? t(($) => $.commons.topNavSearch.placeholder)
                  : t(($) => $.commons.search)
              }
              value={search}
              onValueChange={(value) => {
                latestQueryRef.current = value;
                setSearch(value);
                debouncedSearch(value);
                setOpen(value.length > 0);
              }}
            />
            {search.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                className="absolute top-2 right-2 opacity-50 hover:opacity-100"
                onClick={() => {
                  latestQueryRef.current = "";
                  setSearch("");
                  setOpen(false);
                  setSemanticResults([]);
                  setSemanticLoading(false);
                  requestIdRef.current++;
                  inputRef.current?.focus();
                }}
              >
                <X className="size-4" />
              </Button>
            )}
          </div>
        </PopoverTrigger>
        {search.length > 0 && (
          <PopoverContent
            className="w-lg p-0"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="flex flex-col divide-y">
              <SearchSection title={t(($) => $.library.header)}>
                <CommandList className="max-h-none overflow-hidden">
                  <CommandItem
                    className="cursor-pointer"
                    onSelect={() => {
                      latestQueryRef.current = "";
                      void navigate({
                        pathname: "/documents",
                        search: `?${createSearchParams({ searchTitle: search }).toString()}`,
                      });
                      setSearch("");
                      setOpen(false);
                    }}
                  >
                    <Search className="size-4 shrink-0" />
                    <span className="truncate">{search}</span>
                  </CommandItem>
                  {results.map((doc) => (
                    <CommandItem
                      key={doc.id}
                      value={doc.title + doc.id}
                      onSelect={() => handleSelect(doc)}
                      className="cursor-pointer"
                    >
                      <DocumentIcon
                        type={doc.fileType}
                        className="size-5 shrink-0"
                      />
                      <span className="truncate">{doc.title}</span>
                      <FieldStatus status={doc.status} />
                    </CommandItem>
                  ))}
                </CommandList>
              </SearchSection>

              {isDocumentSearchEnabled && (
                <SearchSection
                  title={t(($) => $.commons.topNavSearch.relevantPassages)}
                >
                  {semanticLoading ? (
                    <SemanticResultSkeleton />
                  ) : semanticResults.length > 0 ? (
                    <div className="flex flex-col gap-1 px-1 pb-2">
                      {semanticResults.map((hit, i) => {
                        const headingPath = getHeadingPath(hit);

                        return (
                          <Link
                            key={`${hit.documentPath}-${i}`}
                            to={hit.documentPath}
                            onClick={() => {
                              latestQueryRef.current = "";
                              setSearch("");
                              setOpen(false);
                            }}
                            className="hover:bg-accent block rounded-md px-2 py-1"
                          >
                            <div className="text-muted-foreground flex items-center gap-1 text-xs">
                              <FileText className="size-4 shrink-0" />
                              <div className="flex min-w-0 items-center gap-1">
                                <span className="truncate font-semibold">
                                  {hit.documentTitle}
                                </span>
                                {headingPath && (
                                  <span className="text-muted-foreground truncate text-xs font-normal">
                                    {`-> ${headingPath}`}
                                  </span>
                                )}
                              </div>
                            </div>

                            <p className="text-sm leading-snug">
                              ...
                              <HighlightText
                                text={
                                  hit.documentCaptionHighlights ??
                                  hit.documentCaption ??
                                  hit.documentSnippet ??
                                  ""
                                }
                                searchWords={searchWords}
                              />
                              ...
                            </p>
                          </Link>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-muted-foreground py-4 text-center text-sm">
                      {t(($) => $.commons.noItems)}
                    </div>
                  )}
                </SearchSection>
              )}
            </div>
          </PopoverContent>
        )}
      </Command>
    </Popover>
  );
};
